import { LocalStorageHelper, utils } from '../index';
import { InstantConfigLoaderParams, InstantConfigResponse } from '../models';
import { delay } from '../utils';
import { fetchTimeout } from './fetch-timeout';

const logGroup = 'instant-config-loader';

const instantConfigKey = 'instantConfig';

const instantConfigProps = {
	b: 'browsers',
	d: 'devices',
	s: 'sampling',
	c: 'samplingCache',
	r: 'regions',
	v: 'value',
};

export class InstantConfigLoader {
	private configPromise: Promise<InstantConfigResponse> = null;
	private storage: LocalStorageHelper;
	private readonly instantConfig: InstantConfigResponse;

	constructor(private readonly params: InstantConfigLoaderParams, container: object = window) {
		this.storage = new utils.localStorageHelper(`instant-config-${params.appName}`, 60000);
		this.instantConfig = this.fromContainer(container);
	}

	async getConfig(): Promise<InstantConfigResponse> {
		if (this.instantConfig) {
			return this.instantConfig;
		}
		if (!this.configPromise) {
			this.configPromise = this.fetchInstantConfig().then((config) => {
				return config ?? this.fetchFallbackConfig();
			});
		}
		return this.configPromise.then((config) => {
			return config;
		});
	}

	private async fetchInstantConfig(fallback = false): Promise<InstantConfigResponse | undefined> {
		const locked = utils.lock();
		if (locked && !fallback) {
			await delay(500);
			// try again, if lock is still present then try LocalStorage or fetch from ICBM
			return this.fetchInstantConfig(true);
		}

		const baseUrl = this.params.instantConfigEndpoint ?? 'https://services.fandom.com';
		const variant = this.params.instantConfigVariant ?? 'icbm';
		const appName = this.params.appName;

		const url = `${baseUrl}/${variant}/api/config?app=${appName}`;

		try {
			const cachedConfigString = this.storage.get();

			if (cachedConfigString) {
				utils.logger(logGroup, 'instant config pulled from local storage', cachedConfigString);
				return JSON.parse(cachedConfigString);
			}

			const response = await fetchTimeout(url, this.params.requestTimeout);

			if (!response.ok) {
				return undefined;
			}

			const instantConfig = await response.json();
			this.storage.set(JSON.stringify(instantConfig));
			utils.release();

			utils.logger(logGroup, 'instant config fetched', instantConfig);

			return instantConfig;
		} catch (err) {
			utils.warner(logGroup, 'could not fetch instant config', err);
			utils.release();
			return undefined;
		}
	}

	private async fetchFallbackConfig(): Promise<InstantConfigResponse> {
		utils.logger(logGroup, 'Fetching fallback config');
		const fallbackEndpoint = this.params.instantConfigFallbackEndpoint;

		if (typeof fallbackEndpoint === 'undefined') {
			return {};
		}

		try {
			utils.logger(logGroup, fallbackEndpoint);
			const response = await fetchTimeout(fallbackEndpoint, this.params.requestTimeout);

			if (!response.ok) {
				return {};
			}

			const fallbackConfig = await response.json();
			utils.logger(logGroup, 'fallback config fetched', fallbackConfig);

			return fallbackConfig;
		} catch (err) {
			utils.warner(logGroup, 'could not fetch fallback config', err);
			return {};
		}
	}

	private fromContainer(container: object): InstantConfigResponse {
		let appConfig: InstantConfigResponse = container[instantConfigKey];

		if (typeof appConfig === 'object' && !appConfig._ready) {
			Object.keys(appConfig).forEach((key) => {
				const value = appConfig[key];
				if (Array.isArray(value)) {
					appConfig[key] = value.map((e) =>
						Object.fromEntries(
							Object.entries(e).map(([k, v]) => [
								instantConfigProps[k] ? instantConfigProps[k] : k,
								v,
							]),
						),
					);
				}
			});
			appConfig._ready = true;
			container[instantConfigKey] = appConfig;
		}
		return appConfig;
	}
}
